import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStyles } from "./OrderStatusStyles";
import { Order, OrderStatus } from "@dining/api-client";
import { Button, Divider, Subtitle2 } from "@fluentui/react-components";
import { appInsights, appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { useNavigate } from "react-router-dom";

interface OrderTimelineProps {
  order?: Order;
}

export const OrderTimelineCard = ({ order }: OrderTimelineProps) => {
  const styles = useStyles();
  const [context, client, orders] = useAppStore((state) => [
    state.context,
    state.client,
    state.orders,
  ]);
  const navigate = useNavigate();
  const { fetch: fetchActiveOrders } = orders;

  const onClick = useCallback(async () => {
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.OrderPickedUp, context, {
      orderId: order?.id,
      orderNumber: order?.orderNumber,
      cafeName: order?.cafeName,
    });

    try {
      await client?.completeOrderPickup(order?.id || "");
      fetchActiveOrders();
      navigate("/buildings");
    } catch (err) {
      appInsights.trackException(
        { exception: err as Error },
        {
          method: "orderPickedUpByUser",
          orderId: order?.id,
          orderNumber: order?.orderNumber,
        },
      );
      throw err;
    }
  }, [navigate, context, appInsights, client, order]);

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "OrderTimelineCard",
    });
  }, [context, appInsights, client]);

  const getLongOrderStatusString = useCallback(() => {
    switch (order?.status) {
      case OrderStatus.SubmittedToVendor:
        return "Your order has been sent to the kitchen.";
      case OrderStatus.BeingPrepared:
        return "Your order is being prepared.";
      case OrderStatus.ReadyForPickup:
        return "Your food is ready for pickup.";
      default:
        return "";
    }
  }, [order]);

  const [liveMessage, setLiveMessage] = useState<string | null>(null);
  const lastMessageRef = useRef<string | null>(null);

  useEffect(() => {
    const msg = getLongOrderStatusString();
    if (msg && msg !== lastMessageRef.current) {
      lastMessageRef.current = msg;
      const outerTimeout = setTimeout(() => {
        setLiveMessage(null); // Clear before updating to ensure screen reader catches it
        const innerTimeout = setTimeout(() => setLiveMessage(msg), 0); // Small delay between updates
        return () => clearTimeout(innerTimeout);
      }, 100);
      return () => clearTimeout(outerTimeout);
    }
  }, [order]);
  const getStepStatus = (step: OrderStatus) => {
    const statusOrder = [
      OrderStatus.SubmittedToVendor,
      OrderStatus.BeingPrepared,
      OrderStatus.ReadyForPickup,
    ];
    const current = statusOrder.indexOf(order?.status ?? OrderStatus.SubmittedToVendor);
    const index = statusOrder.indexOf(step);
    if (index < current) return "completed";
    if (index === current) return "in progress";
    return "not started";
  };

  const renderOrderStatusStep = (step: OrderStatus, label: string) => (
    <div
      className={styles.root}
      role="img"
      aria-label={`${label}. Status: ${getStepStatus(step)}.`}
      tabIndex={0}
    >
      <div className={styles.indicator} style={{ borderRadius: "9999px" }}></div>
    </div>
  );

  return (
    order && (
      <div className={styles.orderTimelineContainer} data-testid="orderTimelineCard">
        <div className={styles.orderTimelineSection}>
          {renderOrderStatusStep(OrderStatus.SubmittedToVendor, "Order sent to kitchen")}
          <Divider
            className={
              order?.status === OrderStatus.SubmittedToVendor
                ? styles.graySeparator
                : styles.purpleSeparator
            }
            aria-hidden="true"
            role="presentation"
          />
          {renderOrderStatusStep(OrderStatus.BeingPrepared, "Order being prepared")}
          <Divider
            className={
              order?.status === OrderStatus.ReadyForPickup
                ? styles.purpleSeparator
                : styles.graySeparator
            }
            aria-hidden="true"
            role="presentation"
          />
          {renderOrderStatusStep(OrderStatus.ReadyForPickup, "Order ready for pickup")}
        </div>

        <div aria-live="polite" aria-atomic="true" className={styles.orderTimelineTextContainer}>
          <span className={styles.srOnly}>{liveMessage}</span>
          <Subtitle2 aria-hidden="true">{liveMessage}</Subtitle2>
        </div>

        {order.status === OrderStatus.ReadyForPickup && (
          <Button
            appearance="primary"
            shape="rounded"
            className={styles.orderPickedUpBox}
            onClick={onClick}
            data-testid="orderPickedUpBtn"
            aria-label="I have picked up my order"
            role="link"
          >
            I have picked up my order
          </Button>
        )}
      </div>
    )
  );
};
